import React from 'react';

/**
 * ReconciliationFilters
 *
 * Controlled filter bar for the Reconciliation Monitor.
 * Combines: issue type filter, time window filter.
 *
 * Props:
 *   issueFilter  string  — one of: 'all' | 'orphaned' | 'failedReconciling' | 'permanentFailed' | 'stuck' | 'slaBreach' | 'needsAttention'
 *   timeFilter   string  — '1h' | '24h' | 'all'
 *   onIssueFilter  (key) => void
 *   onTimeFilter   (key) => void
 *   resultCount  number  — shown on right side
 *   strings      i18n object (strings.reconciliationMonitor)
 */

const ISSUE_FILTERS = [
  { key: 'all',               labelKey: 'filterAll',               defaultLabel: 'All' },
  { key: 'orphaned',          labelKey: 'filterOrphaned',          defaultLabel: 'Orphaned Payments' },
  { key: 'failedReconciling', labelKey: 'filterFailedReconciling', defaultLabel: 'Retrying' },
  { key: 'permanentFailed',   labelKey: 'filterPermanentFailed',   defaultLabel: 'Permanent Failed' },
  { key: 'stuck',             labelKey: 'filterStuck',             defaultLabel: 'Stuck' },
  { key: 'slaBreach',         labelKey: 'filterSlaBreach',         defaultLabel: 'SLA Breached' },
  { key: 'needsAttention',    labelKey: 'filterNeedsAttention',    defaultLabel: 'Needs Attention' },
];

const TIME_FILTERS = [
  { key: '1h',  labelKey: 'timeFilter1h',  defaultLabel: 'Last 1h' },
  { key: '24h', labelKey: 'timeFilter24h', defaultLabel: 'Last 24h' },
  { key: 'all', labelKey: 'timeFilterAll', defaultLabel: 'All time' },
];

export default function ReconciliationFilters({
  issueFilter = 'all',
  timeFilter = '24h',
  onIssueFilter,
  onTimeFilter,
  resultCount,
  strings: s = {},
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Label */}
      <span className="text-xs font-sans text-textSecondary mr-1">
        {s.filterLabel || 'Filter:'}
      </span>

      {/* Issue filters */}
      {ISSUE_FILTERS.map(({ key, labelKey, defaultLabel }) => (
        <button
          key={key}
          onClick={() => onIssueFilter && onIssueFilter(key)}
          className={`px-3 py-1 rounded-xl text-xs font-sans transition-colors border ${
            issueFilter === key
              ? 'bg-secondary text-white border-secondary'
              : 'bg-cardBg border-secondary/20 text-bodyText hover:border-secondary/50'
          }`}
        >
          {s[labelKey] || defaultLabel}
        </button>
      ))}

      {/* Divider */}
      <span className="text-secondary/30 mx-1">|</span>

      {/* Time filters */}
      {TIME_FILTERS.map(({ key, labelKey, defaultLabel }) => (
        <button
          key={`time-${key}`}
          onClick={() => onTimeFilter && onTimeFilter(key)}
          className={`px-3 py-1 rounded-xl text-xs font-sans transition-colors border ${
            timeFilter === key
              ? 'bg-secondary/20 text-secondary border-secondary'
              : 'bg-cardBg border-secondary/20 text-bodyText hover:border-secondary/50'
          }`}
        >
          {s[labelKey] || defaultLabel}
        </button>
      ))}

      {/* Result count */}
      {resultCount != null && (
        <span className="ml-auto text-xs font-sans text-textSecondary">
          {resultCount} {resultCount === 1 ? 'order' : 'orders'}
        </span>
      )}
    </div>
  );
}
